// src/components/ChangePanel.tsx — every change goes edit → Preview → Reason → Apply.
// Right panel on desktop, bottom sheet on mobile. Never a modal: the page stays usable.
import { useEffect, useMemo, useState } from "react";
import { canApply, isPriced, ops, POLICY_LABEL, TRACK_LABEL, type Change, type Cell, type LockWarning, type Preview, type PreviewCell, STORIES_SOURCE_LABEL, type StoriesSource } from "../lib/ops";
import { MONTHS, marginTone, money, pct } from "../lib/format";
import { useOperator, useServices, useToast } from "../lib/context";
import { Margin, Notice, SectionLabel } from "./ui";
import { SheetHandle, useSheetDismiss } from "./Panel";

export interface Pending {
  change: Change;
  title: string;
}

export function ChangePanel(props: { pending: Pending; onClose: () => void; onApplied: () => void | Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const services = useServices();
  const settings = services.data?.settings;
  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of services.data?.services ?? []) m.set(s.id, s.name);
    // SR-B2: track previews label rows "<service>:<track>" (e.g. sea-013:own)
    return (id: string) => {
      const [base, track] = id.split(":");
      const name = m.get(base);
      if (!name) return id;
      return track ? `${name} (${TRACK_LABEL[track] ?? track})` : name;
    };
  }, [services.data]);

  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Re-run the preview whenever the proposed change is replaced.
  const changeKey = JSON.stringify(props.pending.change);
  useEffect(() => {
    let live = true;
    setPreview(null);
    setPreviewError(null);
    setApplyError(null);
    setAck(false);
    ops
      .preview(props.pending.change)
      .then((p) => live && setPreview(p))
      .catch((e: Error) => live && setPreviewError(e.message));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeKey]);

  const belowCostServices = useMemo(() => {
    const ids = new Set<string>();
    for (const c of preview?.cells ?? []) if (isPriced(c.after) && c.after.below_cost) ids.add(c.service_id);
    return Array.from(ids).map(nameOf);
  }, [preview, nameOf]);

  const needsAck = (preview?.summary.below_cost_count ?? 0) > 0;
  const reasonOk = reason.trim().length >= 5;
  const allowed = canApply(role);

  async function apply() {
    setApplying(true);
    setApplyError(null);
    try {
      await ops.apply(props.pending.change, reason.trim(), needsAck && ack);
      toast("✓ Saved");
      await props.onApplied();
      props.onClose();
    } catch (e) {
      setApplyError((e as Error).message);
    } finally {
      setApplying(false);
    }
  }

  const s = preview?.summary;
  const isScope = props.pending.change.type === "service_scope";   // GM-TXT-02 B3: text change, no prices to show
  const isFact = props.pending.change.type === "property_fact";    // GM-TXT-03 D3: house stories, no prices to show
  const sheetRef = useSheetDismiss(props.onClose);
  return (
    <aside ref={sheetRef} className="change-panel" aria-label="Preview change">
      <SheetHandle onClose={props.onClose} />
      <div className="panel-head">
        <div>
          <p className="panel-kicker">{POLICY_LABEL[props.pending.change.type] ?? "Change"}</p>
          <h2>{props.pending.title}</h2>
        </div>
        <button type="button" className="btn quiet" onClick={props.onClose}>
          Close preview
        </button>
      </div>

      <div className="panel-body">
        {!preview && !previewError && <p className="loading">{isFact ? "Checking the house…" : isScope ? "Checking the scope line…" : "Pricing every month at every reference ZIP…"}</p>}
        {previewError && <Notice kind="error">{previewError}</Notice>}

        {preview && s && (
          <>
            {isFact ? <FactDiff before={preview.old} after={preview.new} /> : isScope ? <ScopeDiff before={preview.old} after={preview.new} /> : (<>
            <p className="summary-line">
              Changes {s.services_changed} {s.services_changed === 1 ? "service" : "services"} · lowest margin{" "}
              {pct(s.lowest_margin_pct)} · {s.below_cost_count} below cost · {s.unavailable_cells} unavailable
            </p>
            {s.below_floor_count > 0 && s.below_cost_count === 0 && (
              <Notice kind="info">
                {s.below_floor_count} {s.below_floor_count === 1 ? "cell is" : "cells are"} under the{" "}
                {settings?.margin_floor_pct ?? 25}% margin floor. You can still apply.
              </Notice>
            )}
            <LockWarnings warnings={preview.campaign_lock_warnings ?? []} />
            <PreviewTable cells={preview.cells} nameOf={nameOf} target={settings?.target_margin_pct ?? 35} floor={settings?.margin_floor_pct ?? 25} />
            </>)}

            <SectionLabel>Reason</SectionLabel>
            <textarea
              className="reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this change? This goes in the change log."
              aria-label="Reason for this change"
            />
            {!reasonOk && reason.length > 0 && <p className="hint">At least 5 characters.</p>}

            {needsAck && (
              <label className="ack">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                <span>
                  I understand this prices <strong>{belowCostServices.join(", ")}</strong> below cost for at least one
                  tier.
                </span>
              </label>
            )}

            {applyError && <Notice kind="error">{applyError}</Notice>}

            {allowed ? (
              <button
                type="button"
                className="btn primary wide"
                disabled={!reasonOk || (needsAck && !ack) || applying}
                onClick={apply}
              >
                {applying ? "Applying…" : "Apply change"}
              </button>
            ) : (
              <Notice kind="info">Viewers can preview changes but not apply them.</Notice>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

function CellView({ before, after, target, floor }: { before: Cell | null; after: Cell | null; target: number; floor: number }) {
  if (!after) return <td className="cell">—</td>;
  if (!isPriced(after)) {
    return (
      <td className="cell muted">
        No price <span className="cell-sub">{after.error.replace(/_/g, " ")}</span>
      </td>
    );
  }
  const b = isPriced(before) ? before : null;
  const changed = !b || b.price_cents !== after.price_cents;
  const tone = marginTone(after.margin_pct, after.below_cost, target, floor);
  return (
    <td className={`cell ${after.below_cost ? "below-cost" : ""} ${after.available ? "" : "unavailable"}`}>
      <span className="money">
        {changed && b ? (
          <>
            <s>{money(b.price_cents)}</s> {money(after.price_cents)}
          </>
        ) : (
          money(after.price_cents)
        )}
      </span>
      <span className="cell-sub">
        <Margin value={pct(after.margin_pct)} tone={tone} estimated={after.estimated} />
        {!after.available && <> · not sold</>}
        {after.below_cost && <> · below cost</>}
      </span>
    </td>
  );
}

/** Rows by month for one service, or by service when a change touches many. Columns are ZIPs. */
// GM-TXT-02 B3: a scope change shows the line before and after, per language. No price cells exist for it.
// GM-TXT-03 D3: house stories before → after. Texts show whole floors, rounded up (1.5 → "2 pisos").
function FactDiff({ before, after }: { before: Record<string, unknown> | null; after: Record<string, unknown> | null }) {
  const floors = (v: unknown) => (typeof v === "number" || typeof v === "string") && Number(v) > 0 ? `${Number(v)} (texts say ${Math.ceil(Number(v))})` : "unknown";
  const src = (v: unknown) => (typeof v === "string" ? STORIES_SOURCE_LABEL[v as StoriesSource] ?? v : "—");
  return (
    <div className="scope-diff">
      <p className="summary-line">No prices change. Jobs already paid keep their own copy; new offers for this house use the new value.</p>
      <div className="scope-diff-row">
        <p className="hint">Stories</p>
        <p className="scope-before">{floors(before?.stories)} · {src(before?.stories_source)}</p>
        <p className="scope-after">{floors(after?.stories)} · {src(after?.stories_source)}</p>
      </div>
    </div>
  );
}

function ScopeDiff({ before, after }: { before: Record<string, unknown> | null; after: Record<string, unknown> | null }) {
  const rows: { lang: string; key: "scope_es" | "scope_en" }[] = [{ lang: "Español", key: "scope_es" }, { lang: "English", key: "scope_en" }];
  const show = (v: unknown) => (typeof v === "string" && v ? v : "—");
  return (
    <div className="scope-diff">
      <p className="summary-line">No prices change. Offer texts for this service use the new line.</p>
      {rows.filter((r) => (before?.[r.key] ?? null) !== (after?.[r.key] ?? null)).map((r) => (
        <div key={r.key} className="scope-diff-row">
          <p className="hint">{r.lang}</p>
          <p className="scope-before">{show(before?.[r.key])}</p>
          <p className="scope-after">{show(after?.[r.key])}</p>
        </div>
      ))}
    </div>
  );
}

function PreviewTable(props: { cells: PreviewCell[]; nameOf: (id: string) => string; target: number; floor: number }) {
  const zips = Array.from(new Set(props.cells.map((c) => c.zip)));
  const svcs = Array.from(new Set(props.cells.map((c) => c.service_id)));
  const byService = svcs.length > 1;
  const rowKeys = byService ? svcs : Array.from(new Set(props.cells.map((c) => c.month))).sort((a, b) => Number(a) - Number(b));
  const find = (row: string | number, zip: string) =>
    props.cells.find((c) => c.zip === zip && (byService ? c.service_id === row : c.month === row));
  return (
    <div className="table-scroll">
      <table className="preview-table">
        <thead>
          <tr>
            <th scope="col">{byService ? "Service" : "Month"}</th>
            {zips.map((z) => (
              <th key={z} scope="col">
                {z}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((row) => (
            <tr key={String(row)}>
              <th scope="row">{byService ? props.nameOf(String(row)) : MONTHS[Number(row) - 1]}</th>
              {zips.map((z) => {
                const c = find(row, z);
                return <CellView key={z} before={c?.before ?? null} after={c?.after ?? null} target={props.target} floor={props.floor} />;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** GE-03 A6: active campaigns whose promo price this change would switch off. A warning, never a block. */
function LockWarnings({ warnings }: { warnings: LockWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="notice error" role="alert">
      <p className="texts-head">
        This change stops {warnings.length} active campaign {warnings.length === 1 ? "price" : "prices"} from applying. You can still apply it.
      </p>
      <ul className="plain-list">
        {warnings.map((w) => (
          <li key={`${w.campaign_id}|${w.service_id}|${w.zip}|${w.month}`}>
            {w.service_name} in {MONTHS[Number(w.month.slice(5, 7)) - 1]} ({w.campaign_name}):{" "}
            {w.reason === "unavailable"
              ? "the service would no longer be bookable"
              : <>standard price {money(w.standard_before_cents)} → {money(w.standard_after_cents)}, at or below the {money(w.lock_cents)} promo, so customers would pay the standard price</>}
            .
          </li>
        ))}
      </ul>
    </div>
  );
}
